/**
 * Baut ein Wandbild maßstabsgetreu (Meter) aus dem aufgelösten Zustand:
 * Körper (Leinwand auf Keilrahmen, Platte, Acryl, Papier) + optional Rahmen, Passepartout, Glas, Aufhängung.
 * Ursprung = Mitte der Bildoberfläche, +Z zeigt zum Betrachter, die Wand liegt hinter `backZ`.
 */
import * as THREE from 'three'
import { MM, frameProfile, matBoardGeometry, metricBox, mitredFrameGeometry } from './geometry.ts'
import { applySurface, buildMaterial } from './materials.ts'
import type { Preview3dBody, Preview3dLibrary, ResolvedPreview3d } from './types.ts'

export type ArtworkInput = {
  /** Bildausschnitt (sichtbare Vorderseite; bei „umgeschlagen“ inkl. Kante) */
  image: HTMLCanvasElement
  widthMm: number
  heightMm: number
  state: ResolvedPreview3d
  library: Preview3dLibrary
  /** Nach nachgeladenen Texturen neu rendern */
  onUpdate?: () => void
}

export type Artwork = {
  group: THREE.Group
  /** Außenmaße inkl. Rahmen (m) */
  outerWidth: number
  outerHeight: number
  /** hinterste Z-Koordinate (Wandabstand) */
  backZ: number
  dispose: () => void
}

export function buildArtwork(input: ArtworkInput): Artwork {
  const { state, library, onUpdate } = input
  const body = state.body
  const frame = state.frame
  const group = new THREE.Group()
  group.name = 'artwork'
  const disposables: Array<{ dispose: () => void }> = []
  const track = <T extends { dispose: () => void }>(x: T) => (disposables.push(x), x)
  const mat = (slug: string, opts: Parameters<typeof buildMaterial>[2] = {}) => track(buildMaterial(library, slug, opts, onUpdate))

  const W = input.widthMm * MM
  const H = input.heightMm * MM
  // Passepartout: Ränder an der langen / kurzen Bildseite (hoch oder quer egal)
  const classic = !!frame && frame.type === 'classic'
  const matLong = classic ? Math.max(0, frame!.matLongMm) * MM : 0
  const matShort = classic ? Math.max(0, frame!.matShortMm) * MM : 0
  const matX = W >= H ? matLong : matShort
  const matY = W >= H ? matShort : matLong
  const hasMat = matX > 0 || matY > 0
  const T = body.kind === 'paper' ? 0.0003 : Math.max(0.0005, body.thicknessMm * MM)

  const tex = (c: HTMLCanvasElement) => {
    const t = track(new THREE.CanvasTexture(c))
    t.colorSpace = THREE.SRGBColorSpace
    t.anisotropy = 8
    return t
  }

  // ---------- Bildflächen ----------
  const edgePx = (W > 0 ? T / W : 0) * input.image.width
  const front = body.edge === 'wrap' && body.kind === 'canvas' ? innerCrop(input.image, edgePx) : input.image
  const frontTex = tex(front)

  let backZ = -T
  if (body.kind === 'canvas') backZ = buildCanvas(group, body, W, H, T, frontTex, input.image, edgePx, mat, tex)
  else if (body.kind === 'plate') backZ = buildPlate(group, body, W, H, T, frontTex, input.image, edgePx, mat, tex)
  else if (body.kind === 'acrylic') backZ = buildAcrylic(group, body, W, H, T, frontTex, mat)
  else backZ = buildPaper(group, body, W, H, frontTex, mat, frame?.type === 'classic')

  if (body.kind !== 'paper' || !frame) backZ = addHanging(group, body, W, H, backZ, mat)

  // ---------- Rahmen ----------
  let outerW = W
  let outerH = H
  if (frame) {
    const bodyDepthMm = -backZ / MM
    const frameMat = mat(frame.material)
    frameMat.side = THREE.DoubleSide

    if (frame.type === 'poster-rail') {
      const f = frame.faceWidthMm * MM, d = frame.depthMm * MM, r = frame.rebateMm * MM
      for (const sgn of [1, -1]) {
        const rail = new THREE.Mesh(metricBox(W + 0.01, f, d), frameMat)
        rail.position.set(0, sgn * (H / 2 - r + f / 2), 0)
        group.add(rail)
      }
      addCord(group, W, H / 2 - r + f, mat)
      outerW = W + 0.01
      outerH = H + 2 * (f - r)
    } else {
      const contentW = W + 2 * matX
      const contentH = H + 2 * matY
      const lift = hasMat ? 1.5 * MM : 0
      if (hasMat) {
        const r = frame.rebateMm * MM
        const board = new THREE.Mesh(matBoardGeometry(contentW + 2 * r, contentH + 2 * r, W, H, lift), mat(frame.matMaterial || 'passepartout-weiss'))
        board.position.z = lift
        group.add(board)
      }
      const { points, shade } = frameProfile(frame, frame.type === 'shadow-gap' ? bodyDepthMm : Math.max(3, bodyDepthMm))
      // Gehrungsleisten tragen Vertexfarben (abgedunkelte Innenflächen der Fuge). Kein clone():
      // nachgeladene Texturen landen im Original-Material.
      frameMat.vertexColors = true
      const mesh = new THREE.Mesh(mitredFrameGeometry(contentW, contentH, points, shade), frameMat)
      mesh.position.z = lift
      group.add(mesh)
      if (frame.type === 'classic') {
        // Rückwand + Glas
        const r = frame.rebateMm * MM
        const backboard = new THREE.Mesh(metricBox(contentW + 2 * r - 0.002, contentH + 2 * r - 0.002, 0.003), mat('rueckwand-hdf'))
        backboard.position.z = backZ - 0.0015
        group.add(backboard)
        backZ -= 0.003
        if (frame.glassMaterial) {
          const glass = new THREE.Mesh(new THREE.PlaneGeometry(contentW + 2 * r, contentH + 2 * r), mat(frame.glassMaterial))
          glass.position.z = lift + 0.001
          glass.castShadow = false
          group.add(glass)
        }
      }
      const gap = frame.type === 'shadow-gap' ? frame.gapMm * MM : -frame.rebateMm * MM
      outerW = contentW + 2 * (gap + frame.faceWidthMm * MM)
      outerH = contentH + 2 * (gap + frame.faceWidthMm * MM)
      const frameBack = points.reduce((m, p) => Math.min(m, p[1]), 0) * MM + lift
      backZ = Math.min(backZ, frameBack)
    }
  }

  group.traverse((o: THREE.Object3D) => {
    if ((o as THREE.Mesh).isMesh) {
      o.castShadow = true
      o.receiveShadow = true
    }
  })

  return {
    group,
    outerWidth: outerW,
    outerHeight: outerH,
    backZ,
    dispose: () => {
      group.traverse((o: any) => o.geometry?.dispose?.())
      disposables.forEach((d) => d.dispose())
    },
  }
}

type MatFn = (slug: string, opts?: Parameters<typeof buildMaterial>[2]) => THREE.MeshPhysicalMaterial
type TexFn = (c: HTMLCanvasElement) => THREE.CanvasTexture

// ---------------------------------------------------------------- Körper

function buildCanvas(g: THREE.Group, b: Preview3dBody, W: number, H: number, T: number, frontTex: THREE.Texture, image: HTMLCanvasElement, edgePx: number, mat: MatFn, tex: TexFn): number {
  const fabric = b.faceMaterial || 'leinwand'
  const faceMat = mat(fabric, { printMap: frontTex })
  applySurface(faceMat, b.surface)
  const front = new THREE.Mesh(new THREE.PlaneGeometry(W, H), faceMat)
  g.add(front)

  addEdges(g, b, W, H, T, image, edgePx, (map, color) => {
    const m = mat(fabric, map ? { printMap: map } : { color })
    applySurface(m, b.surface)
    return m
  }, tex, 0)

  // Keilrahmen: Leisten hinter dem Gewebe
  const bw = Math.min(b.barWidthMm * MM, Math.min(W, H) / 3)
  const bd = T - 0.0015
  const wood = mat(b.supportMaterial || 'kiefer-roh')
  const woodX = mat(b.supportMaterial || 'kiefer-roh', { rotate: true })
  const z = -0.0008 - bd / 2
  const inset = 0.0006
  const bar = (w: number, h: number, x: number, y: number, m: THREE.Material) => {
    const mesh = new THREE.Mesh(metricBox(w, h, bd), m)
    mesh.position.set(x, y, z)
    g.add(mesh)
  }
  bar(W - 2 * inset, bw, 0, H / 2 - bw / 2 - inset, wood)
  bar(W - 2 * inset, bw, 0, -H / 2 + bw / 2 + inset, wood)
  bar(bw, H - 2 * bw - 2 * inset, -W / 2 + bw / 2 + inset, 0, woodX)
  bar(bw, H - 2 * bw - 2 * inset, W / 2 - bw / 2 - inset, 0, woodX)
  // Mittelstreben ab 1 m Kantenlänge
  if (W > 1.0) bar(bw * 0.8, H - 2 * bw, 0, 0, woodX)
  if (H > 1.0) bar(W - 2 * bw, bw * 0.8, 0, 0, wood)
  // Keile in den Ecken
  const wedge = new THREE.BoxGeometry(0.018, 0.012, 0.004)
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
    const k = new THREE.Mesh(wedge, wood)
    k.position.set(sx * (W / 2 - bw - 0.006), sy * (H / 2 - bw - 0.002), -T + 0.002)
    k.rotation.z = sx * sy * Math.PI / 4
    g.add(k)
  }

  // Gewebe-Umschlag auf der Rückseite + Tackernadeln
  const fold = Math.min(0.018, bw * 0.6)
  const backFabric = mat(b.backMaterial || 'leinwand', { color: '#efe8da' })
  const ring = new THREE.Mesh(ringGeometry(W, H, fold), backFabric)
  ring.rotation.y = Math.PI
  ring.position.z = -T - 0.0004
  g.add(ring)
  const staple = new THREE.BoxGeometry(0.012, 0.0012, 0.0008)
  const steel = mat('alu-gebuerstet')
  const step = 0.09
  for (let x = -W / 2 + 0.06; x <= W / 2 - 0.06; x += step) {
    for (const sy of [-1, 1]) {
      const s = new THREE.Mesh(staple, steel)
      s.position.set(x, sy * (H / 2 - fold / 2), -T - 0.0008)
      g.add(s)
    }
  }
  for (let y = -H / 2 + 0.06; y <= H / 2 - 0.06; y += step) {
    for (const sx of [-1, 1]) {
      const s = new THREE.Mesh(staple, steel)
      s.rotation.z = Math.PI / 2
      s.position.set(sx * (W / 2 - fold / 2), y, -T - 0.0008)
      g.add(s)
    }
  }
  return -T - 0.001
}

function buildPlate(g: THREE.Group, b: Preview3dBody, W: number, H: number, T: number, frontTex: THREE.Texture, image: HTMLCanvasElement, edgePx: number, mat: MatFn, tex: TexFn): number {
  const faceMat = b.faceMaterial ? mat(b.faceMaterial, { printMap: frontTex }) : mat('papier-matt', { printMap: frontTex })
  if (!b.faceMaterial) faceMat.roughness = 0.6
  applySurface(faceMat, b.surface)
  const front = new THREE.Mesh(new THREE.PlaneGeometry(W, H), faceMat)
  front.position.z = 0.00005
  g.add(front)
  const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), mat(b.backMaterial || 'alu-gebuerstet'))
  back.rotation.y = Math.PI
  back.position.z = -T
  g.add(back)
  addEdges(g, b, W, H, T, image, edgePx, (map, color) => (map ? mat(b.edgeMaterial || 'hartschaum-weiss', { printMap: map }) : mat(b.edgeMaterial || 'alu-gebuerstet', color ? { color } : {})), tex, 0)
  return -T
}

function buildAcrylic(g: THREE.Group, b: Preview3dBody, W: number, H: number, T: number, frontTex: THREE.Texture, mat: MatFn): number {
  // Druck hinter dem Acrylglas, glänzend; Acryl als Transmissions-Material davor
  const printMat = mat('papier-matt', { printMap: frontTex })
  printMat.roughness = 0.35
  const print = new THREE.Mesh(new THREE.PlaneGeometry(W, H), printMat)
  print.position.z = -T - 0.0002
  g.add(print)
  const acrylMat = mat(b.faceMaterial || 'acrylglas')
  acrylMat.thickness = T
  const glass = new THREE.Mesh(metricBox(W, H, T), acrylMat)
  glass.position.z = -T / 2
  glass.castShadow = false
  g.add(glass)
  // Träger (Alu-Verbund) hinter dem Druck
  const backer = new THREE.Mesh(metricBox(W - 0.001, H - 0.001, 0.002), mat(b.backMaterial || 'alu-gebuerstet'))
  backer.position.z = -T - 0.0013
  g.add(backer)
  return -T - 0.0023
}

function buildPaper(g: THREE.Group, b: Preview3dBody, W: number, H: number, frontTex: THREE.Texture, mat: MatFn, framed: boolean): number {
  const m = mat(b.faceMaterial || 'papier-matt', { printMap: frontTex })
  applySurface(m, b.surface)
  if (framed) {
    const p = new THREE.Mesh(new THREE.PlaneGeometry(W, H), m)
    g.add(p)
    return -0.0005
  }
  // freies Poster: leicht gewellt
  const geo = new THREE.PlaneGeometry(W, H, 24, 2)
  const pos = geo.getAttribute('position') as THREE.BufferAttribute
  for (let i = 0; i < pos.count; i++) pos.setZ(i, Math.sin((pos.getX(i) / W) * Math.PI * 3) * 0.0012)
  geo.computeVertexNormals()
  const p = new THREE.Mesh(geo, m)
  g.add(p)
  return -0.002
}

/** Kanten (oben/unten/links/rechts) je Kantenmodus */
function addEdges(g: THREE.Group, b: Preview3dBody, W: number, H: number, T: number, image: HTMLCanvasElement, edgePx: number, makeMat: (map: THREE.Texture | null, color?: string) => THREE.Material, tex: TexFn, z0: number) {
  const px = Math.max(2, Math.round(edgePx))
  const strip = (side: 'top' | 'bottom' | 'left' | 'right') => {
    if (b.edge === 'white') return makeMat(null, '#f7f6f2')
    if (b.edge === 'color') return makeMat(null, b.kind === 'canvas' ? b.edgeColor : undefined)
    return makeMat(tex(edgeStrip(image, side, px, b.edge === 'mirror')))
  }
  const zc = z0 - T / 2
  const top = new THREE.Mesh(new THREE.PlaneGeometry(W, T), strip('top'))
  top.rotation.x = -Math.PI / 2
  top.position.set(0, H / 2, zc)
  const bottom = new THREE.Mesh(new THREE.PlaneGeometry(W, T), strip('bottom'))
  bottom.rotation.x = Math.PI / 2
  bottom.position.set(0, -H / 2, zc)
  const left = new THREE.Mesh(new THREE.PlaneGeometry(T, H), strip('left'))
  left.rotation.y = -Math.PI / 2
  left.position.set(-W / 2, 0, zc)
  const right = new THREE.Mesh(new THREE.PlaneGeometry(T, H), strip('right'))
  right.rotation.y = Math.PI / 2
  right.position.set(W / 2, 0, zc)
  g.add(top, bottom, left, right)
}

function addHanging(g: THREE.Group, b: Preview3dBody, W: number, H: number, backZ: number, mat: MatFn): number {
  if (b.hanging === 'rail') {
    // Aufhängeschiene (umlaufendes Alu-Profil) 15 mm tief, 5 cm eingerückt
    const m = mat('alu-gebuerstet')
    const inset = Math.min(0.05, Math.min(W, H) * 0.15)
    const w = W - 2 * inset, h = H - 2 * inset, s = 0.012, d = 0.015
    const add = (bw: number, bh: number, x: number, y: number) => {
      const r = new THREE.Mesh(metricBox(bw, bh, d), m)
      r.position.set(x, y, backZ - d / 2)
      g.add(r)
    }
    add(w, s, 0, h / 2 - s / 2); add(w, s, 0, -h / 2 + s / 2)
    add(s, h - 2 * s, -w / 2 + s / 2, 0); add(s, h - 2 * s, w / 2 - s / 2, 0)
    return backZ - d
  }
  if (b.hanging === 'hooks') {
    const m = mat('alu-gebuerstet')
    for (const sx of [-1, 1]) {
      const hk = new THREE.Mesh(metricBox(0.03, 0.012, 0.004), m)
      hk.position.set(sx * W * 0.3, H / 2 - 0.03, backZ - 0.002)
      g.add(hk)
    }
    return backZ - 0.004
  }
  if (b.hanging === 'cord') addCord(g, W, H / 2, mat)
  return backZ
}

function addCord(g: THREE.Group, W: number, topY: number, mat: MatFn) {
  const m = mat('lack-schwarz')
  const apex = new THREE.Vector3(0, topY + Math.min(0.2, W * 0.25), -0.004)
  for (const sx of [-1, 1]) {
    const from = new THREE.Vector3(sx * W * 0.42, topY - 0.005, -0.004)
    const curve = new THREE.LineCurve3(from, apex)
    const tube = new THREE.Mesh(new THREE.TubeGeometry(curve, 1, 0.0008, 6, false), m)
    g.add(tube)
  }
}

// ---------------------------------------------------------------- Bildhelfer

function innerCrop(image: HTMLCanvasElement, edgePx: number): HTMLCanvasElement {
  const e = Math.max(0, Math.min(Math.round(edgePx), Math.floor(Math.min(image.width, image.height) / 4)))
  if (e <= 0) return image
  const c = document.createElement('canvas')
  c.width = image.width - 2 * e
  c.height = image.height - 2 * e
  c.getContext('2d')!.drawImage(image, e, e, c.width, c.height, 0, 0, c.width, c.height)
  return c
}

/**
 * Kantenstreifen: `mirror` = Rand gespiegelt (Spiegelung an der Bildkante),
 * sonst umgeschlagen (der äußere Bildstreifen läuft um die Kante).
 * Ausgerichtet so, dass die Streifenkante an der Bildvorderkante anliegt.
 */
function edgeStrip(image: HTMLCanvasElement, side: 'top' | 'bottom' | 'left' | 'right', px: number, mirror: boolean): HTMLCanvasElement {
  const { width: w, height: h } = image
  const c = document.createElement('canvas')
  const ctx = c.getContext('2d')!
  const p = Math.min(px, Math.floor(Math.min(w, h) / 3))
  if (side === 'top' || side === 'bottom') {
    c.width = w
    c.height = p
    const sy = side === 'top' ? 0 : h - p
    // Gespiegelt: die Bildkante liegt an der Vorderkante an; umgeschlagen: der innere Streifen
    if (mirror) { ctx.translate(0, p); ctx.scale(1, -1) }
    ctx.drawImage(image, 0, sy, w, p, 0, 0, w, p)
  } else {
    c.width = p
    c.height = h
    const sx = side === 'left' ? 0 : w - p
    if (mirror) { ctx.translate(p, 0); ctx.scale(-1, 1) }
    ctx.drawImage(image, sx, 0, p, h, 0, 0, p, h)
  }
  return c
}

/** Rechteckiger Ring (Außen W×H, Breite b) als flache Geometrie */
function ringGeometry(W: number, H: number, b: number): THREE.ShapeGeometry {
  const s = new THREE.Shape()
  s.moveTo(-W / 2, -H / 2); s.lineTo(W / 2, -H / 2); s.lineTo(W / 2, H / 2); s.lineTo(-W / 2, H / 2); s.closePath()
  const hole = new THREE.Path()
  hole.moveTo(-W / 2 + b, -H / 2 + b); hole.lineTo(-W / 2 + b, H / 2 - b); hole.lineTo(W / 2 - b, H / 2 - b); hole.lineTo(W / 2 - b, -H / 2 + b); hole.closePath()
  s.holes.push(hole)
  return new THREE.ShapeGeometry(s)
}
